import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import type { ReactNode } from 'react';

import type { OptionType } from '@/api/common';
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxStatus,
  ComboboxValue,
  type ComboboxProps,
} from '@/components/Form/pickers';

export type ComboboxPickerMultipleProps<T extends OptionType> = Omit<
  ComboboxProps<T, true>,
  | 'items'
  | 'filteredItems'
  | 'value'
  | 'onValueChange'
  | 'itemToStringValue'
  | 'itemToStringLabel'
  | 'isItemEqualToValue'
> & {
  items?: readonly T[];
  filteredItems?: readonly T[];
  value?: T[];
  className?: string;
  disabled?: boolean;
  isLoading?: boolean;
  error?: Error | null;
  placeholder?: string;
  loadingPlaceholder?: string;
  notFoundContent?: ReactNode;
  renderOption?: (option: T) => ReactNode;
};

const ComboboxPickerMultiple = <T extends OptionType>({
  items,
  filteredItems,
  value,
  className,
  disabled = false,
  isLoading = false,
  error,
  placeholder = t`Select...`,
  loadingPlaceholder = t`Loading...`,
  notFoundContent,
  renderOption,
  'aria-invalid': ariaInvalid,
  ...rest
}: ComboboxPickerMultipleProps<T>) => {
  return (
    <Combobox
      {...rest}
      multiple
      items={items}
      filteredItems={filteredItems}
      itemToStringValue={(item) => item.id}
      itemToStringLabel={(item) => item.name ?? ''}
      isItemEqualToValue={(item, selected) => item.id === selected.id}
      isLoading={isLoading}
      error={error}
      disabled={disabled}
      value={value ?? []}
    >
      <ComboboxChips className={className} aria-invalid={ariaInvalid}>
        <ComboboxValue>
          {(value ?? []).map((item) => (
            <ComboboxChip key={item.id}>{item.name}</ComboboxChip>
          ))}
        </ComboboxValue>
        <ComboboxChipsInput placeholder={isLoading ? loadingPlaceholder : placeholder} />
      </ComboboxChips>
      <ComboboxContent>
        <ComboboxStatus />
        <ComboboxEmpty>{notFoundContent ?? <Trans>No items were found...</Trans>}</ComboboxEmpty>
        <ComboboxList>
          {(item: T) => (
            <ComboboxItem key={item.id} value={item} disabled={item.disabled}>
              {renderOption ? renderOption(item) : item.name}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
};

export default ComboboxPickerMultiple;
